import { AttractionTypeDropdown } from '@/components/dropdowns';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@startupquickstart/frontend';
import { useEffect, useState } from 'react';

interface MergeAttractionTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Name of the source type being merged away (shown in the warning copy). */
  sourceName: string;
  loading?: boolean;
  /** Called with the chosen target type id when the user confirms. */
  onConfirm: (targetTypeId: string) => void | Promise<void>;
}

/**
 * Confirmation dialog for merging one attraction type into another. The user
 * picks a target type; on confirm, all of the source type's attractions are
 * moved to it and the source type is deleted (handled by the caller).
 */
export function MergeAttractionTypeDialog({
  open,
  onOpenChange,
  sourceName,
  loading = false,
  onConfirm,
}: MergeAttractionTypeDialogProps) {
  const [targetTypeId, setTargetTypeId] = useState('');

  // Clear the selection each time the dialog opens.
  useEffect(() => {
    if (open) setTargetTypeId('');
  }, [open]);

  const handleConfirm = async () => {
    if (!targetTypeId) return;
    await onConfirm(targetTypeId);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Merge "{sourceName}" into another type</DialogTitle>
          <DialogDescription>
            Every attraction on <strong>{sourceName}</strong> will be moved to the
            selected type, and <strong>{sourceName}</strong> will be deleted. This
            can't be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="py-1">
          <label className="mb-1.5 block text-sm font-medium">Target type</label>
          <AttractionTypeDropdown
            value={targetTypeId}
            onChange={setTargetTypeId}
            placeholder="Select a type to merge into"
          />
        </div>

        <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!targetTypeId || loading}
            loading={loading}
          >
            Merge &amp; delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
